import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

import { getProductionGroups } from "@/api/selection/production-group-api";
import { type ProductionGroup, productionGroupSchema } from "@/schemas";

const REFETCH_INTERVAL = 15 * 1000;

export const PRODUCTION_GROUPS_QUERY_KEY = ["production-groups"] as const;

const productionGroupsSchema = z.array(productionGroupSchema);

const fetchProductionGroups = async (): Promise<ProductionGroup[] | null> => {
	try {
		const data = await getProductionGroups();

		// TODO: Move validation to API layer after end-to-end testing on all components
		const validatedData = productionGroupsSchema
			.enrich({ callSite: "fetchProductionGroups" })
			.safeParseWithTelemetry(data);

		if (!validatedData.success) {
			return null;
		}

		return validatedData.data;
	} catch {
		return null;
	}
};

type UseProductionGroupsOptions = Omit<
	UseQueryOptions<ProductionGroup[] | null, Error, ProductionGroup[] | null, readonly string[]>,
	"queryKey" | "queryFn"
>;

export const useProductionGroups = (queryConfig: UseProductionGroupsOptions = {}) => {
	const query = useQuery({
		queryKey: PRODUCTION_GROUPS_QUERY_KEY,
		queryFn: fetchProductionGroups,
		refetchInterval: REFETCH_INTERVAL,
		staleTime: 10 * 1000,
		...queryConfig,
	});

	return {
		productionGroups: query.data,
		refresh: query.refetch,
		isLoading: query.isLoading,
		error: query.error,
	};
};
